<template>
  <b-modal
    ref="modalPackagingSettings"
    body-class="!p-10 text-black w-full"
    size="lg"
    centered
    hide-footer
    hide-header
    hide-header-close
    no-close-on-backdrop
  >
    <div class="flex items-center flex-wrap gap-[8px] justify-between mb-[24px]">
      <h3 class="text-lg text-center font-semibold text-black">
        Packaging
      </h3>
      <div class="flex gap-[8px]">
        <b-button
          variant="outline-primary"
          class="!text-[14px] !w-full"
          @click="hide"
        >
          Batal
        </b-button>
        <b-button
          :variant="isSaveButtonDisabled ? 'secondary' : 'primary'"
          class="!text-[14px] !w-full !flex items-center justify-center gap-2"
          :disabled="isSaveButtonDisabled"
          @click="handleSave"
        >
          <b-spinner
            v-if="isMutating"
            small
            variant="white"
          />
          {{ isMutating ? 'Menyimpan...' : 'Simpan' }}
        </b-button>
      </div>
    </div>
    <div class="mt-[8px] bg-[#F5F5F5] p-[16px] rounded-md">
      <div class="mb-[16px]">
        <div
          v-for="(packing, index) in selectedPackingMaterials"
          :key="`packings-${index}`"
          class="c-packings flex flex-wrap gap-[16px] mb-[14px]"
        >
          <b-button
            variant="danger"
            class="!py-[2px] !px-[8px] !text-[16px] !py-[10px] !size-[35px]"
            :disabled="thereIsOnlyOnePackingMaterial"
            @click="removePackingMaterial(index)"
          >
            <span class="k-trash text-[14px]" />
          </b-button>
          <div class="c-select-qty flex-1 flex flex-row items-center">
            <infinite-select
              v-model="selectedPackingMaterials[index]"
              placeholder="Bahan Packing"
              class="flex-1 bg-white"
              option-label="name"
              :default-value="defaultPackings[index]"
              :options="getMaterialOptions(packing)"
              :has-next-page="fetcher.hasMore || false"
              :debounce-timeout="500"
              :loading="fetcher.loading || false"
              :clearable="false"
              :reduce="reducePackingMaterial"
              @on-search="handleSearchPacking"
              @on-intersect="handleIntersect"
            >
              <template #no-options="{ search, searching }">
                <template v-if="searching">
                  Bahan packing untuk <em>'{{ search }}'</em> tidak ditemukan.
                </template>
                <em v-else>Masukan bahan packing yang valid</em>
              </template>
            </infinite-select>
            <div class="w-full c-qty">
              <b-input-group class="w-full">
                <b-input-group-prepend>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="!p-[6px] !bg-white"
                    :disabled="isOnMinimumLayer(packing)"
                    @click="handleChangePackingLayer(packing, 'minus')"
                  >
                    <span class="k-minus text-[14px]" />
                  </b-button>
                </b-input-group-prepend>
                <b-form-input
                  v-model="packing.layer"
                  type="number"
                  number
                  min="1"
                  placeholder="0"
                  :formatter="formatNumber"
                  @input="handleChangePackingLayerInput()"
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="!p-[6px] !bg-white"
                    @click="handleChangePackingLayer(packing, 'add')"
                  >
                    <span class="k-add text-[14px]" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
          </div>
        </div>
      </div>

      <b-button
        size="lg"
        variant="primary"
        class="!text-[14px] !w-full"
        :disabled="thereIsEmptyPackingMaterial"
        @click="addPackingMaterial"
      >
        Tambah Bahan Packing
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { mapState } from 'vuex'
import InfiniteSelect from '@/views/components/select/InfiniteSelect.vue'
import { toastError } from '@/libs/toastification'

/**
 * Modal for updating the order packing material
 * @prop {Object} fetcher - The fetcher object for fetching packing materials
 * @returns {void}
 */
export default {
  name: 'ModalPackagingSettings',
  components: {
    InfiniteSelect,
  },
  props: {
    fetcher: {
      type: Object,
      required: false,
      default: () => ({
        data: [],
        hasMore: false,
        loading: false,
      }),
    },
  },
  data() {
    return {
      payload: undefined,
      position: undefined,
      order_number: undefined,
      selectedPackingMaterials: [],
      isMutating: false,
      defaultPackings: [],
      hasPackingLayerChanged: false,
      packingOptions: [],
    }
  },
  computed: {
    ...mapState('warehouse', ['profile']),
    thereIsOnlyOnePackingMaterial() {
      return this.selectedPackingMaterials.length === 1
    },
    thereIsEmptyPackingMaterial() {
      return this.selectedPackingMaterials.some(val => !val.name || !val.layer)
    },
    isSaveButtonDisabled() {
      return !this.hasPackingLayerChanged || this.thereIsEmptyPackingMaterial || this.isMutating
    },
  },
  methods: {
    /**
     * Get the warehouse ID
     * @returns {Promise<Number>} - The warehouse ID
     */
    async warehouseId() {
      if (!this.profile) {
        const data = await this.$store.dispatch('warehouse/getWarehouseProfile')
        return data?.id
      }
      return this.profile?.id
    },
    /**
     * Format the material options for the current packing to include the disabled state
     * @param currentPacking {Object} - The current packing
     * @returns {Array} - The material options with disabled state
     */
    getMaterialOptions(currentPacking) {
      return this.packingOptions.map(val => ({
        ...val,
        disabled: this.selectedPackingMaterials.some((selected, index) => selected.id === val.id && selected.id !== currentPacking.id),
      })) || []
    },
    /**
     * Exposed method to show the modal from the parent component
     * @param data {Object} - The data object containing the position, order number, and packings
     * @param position {Number} - The position of the order
     * @param orderNumber {String} - The order number
     * @param packings {Array} - The packings array
     * @returns {void}
     */
    show({
      data, position, orderNumber, packings, totalOrder,
    }) {
      this.position = position
      this.order_number = orderNumber
      this.packingOptions = this.fetcher.data

      if (packings && packings.length > 0) {
        const formattedPackings = packings.map(val => ({
          name: val.name,
          layer: val.layer,
          id: val.id,
        }))
        this.defaultPackings = formattedPackings
        this.selectedPackingMaterials = _.cloneDeep(formattedPackings)
      } else {
        this.selectedPackingMaterials = [{
          id: 0,
          name: undefined,
          layer: undefined,
        }]
      }

      const products = data.products?.map(val => ({
        product_id: val.product_id,
        total_item: val.total_item,
        is_product_bonus: val.is_product_bonus,
        is_bundling: val.is_bundling,
        bundle_components: val.product_bundles?.map(bundle => ({
          product_id: bundle.product_id,
          qty: bundle.qty || 1,
          is_product_bonus: bundle.is_product_bonus,
        })),
      }))

      this.payload = {
        total_order: totalOrder,
        packings: this.selectedPackingMaterials,
        products,
      }

      this.$refs.modalPackagingSettings.show()
    },
    /**
     * Exposed method to hide the modal from the parent component
     * @returns {void}
     */
    hide() {
      this.selectedPackingMaterials = []
      this.$refs.modalPackagingSettings.hide()
    },
    /**
     * Handle the process of saving the order packing material
     * @returns {void}
     */
    async handleSave() {
      this.isMutating = true
      try {
        this.payload = {
          ...this.payload,
          packings: this.selectedPackingMaterials.map(val => ({
            id: val.id,
            total_layer: val.layer,
          })),
        }

        const recalculateResult = await this.recalculateFulfillment(this.payload)

        const data = {
          ...recalculateResult,
          packings: this.selectedPackingMaterials,
        }
        this.$emit('on-save', { position: this.position, order_number: this.order_number, data })
        this.hide()
      } catch (error) {
        toastError('Gagal mengubah bahan packing, silahkan coba lagi')
      } finally {
        this.isMutating = false
      }
    },
    async recalculateFulfillment(payload) {
      const warehouseId = await this.warehouseId()
      const response = await this.$http_new.post('/komship/api/v2/orders/recalculate-fulfillment', { ...payload, warehouse_id: warehouseId })
      return response.data.data
    },
    /**
     * Handle the process of searching for packing materials
     * @param search {String} - The search query
     * @returns {void}
     */
    async handleSearchPacking(search) {
      if (!search) {
        this.packingOptions = this.fetcher.data
        return
      }
      const searchResults = this.fetcher.data.filter(val => val.name.toLowerCase().includes(search.toLowerCase()))
      if (this.fetcher.hasMore || (searchResults.length === 0 && this.fetcher.hasMore)) {
        await this.fetcher.fetchData()
        this.handleSearchPacking(search)
        return
      }
      this.packingOptions = searchResults
    },
    handleIntersect() {
      if (this.fetcher && this.fetcher.fetchData) {
        this.fetcher.fetchData()
      }
    },
    addPackingMaterial() {
      this.selectedPackingMaterials.push({
        id: 0,
        name: undefined,
        layer: undefined,
      })
    },
    handleChangePackingLayerInput() {
      this.hasPackingLayerChanged = true
    },
    removePackingMaterial(index) {
      this.hasPackingLayerChanged = true
      this.selectedPackingMaterials.splice(index, 1)
      this.$emit('packing-materials-changed', this.selectedPackingMaterials)
    },
    isOnMinimumLayer(packing) {
      return packing.layer <= 1
    },
    handleChangePackingLayer(packing, type) {
      if (!packing) return
      let currentLayer = packing.layer || 0
      if (type === 'minus' && currentLayer > 1) currentLayer -= 1
      if (type === 'add') currentLayer += 1
      this.hasPackingLayerChanged = true
      this.$set(packing, 'layer', currentLayer)
    },
    /**
     * Reduce the packing material to the required format for the selection component
     * @param val {Object} - The packing material object
     * @returns {Object} - The reduced packing material object
     */
    reducePackingMaterial(val) {
      return {
        name: val.name,
        layer: val.layer,
        id: val.id,
      }
    },
    formatNumber(value) {
      return Number(value.replace(/[^0-9]/g, ''))
    },
  },
}
</script>

<style scoped>
.c-packings {
  align-items: flex-center;
}
.c-select-qty {
  flex-direction: row;
  gap: 16px;
}
.c-qty {
  max-width: 120px;
}
@media (max-width: 470px) {
  .c-packings {
    align-items: flex-start;
  }
  .c-select-qty {
    flex-direction: column!important;
    gap: 8px;
  }
  .c-select-qty .v-select {
    width: 100%!important;
  }
  .c-qty {
    max-width: 100%;
  }
}
</style>
